import React, { useEffect, useRef } from "react";
import { createChart, ColorType, CrosshairMode } from "lightweight-charts";

export default function CandlestickChart({ data, priceLines = [], markers = [], trades = [] }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: "#0F0F0F" },
        textColor: "#A1A1AA",
        fontFamily: "IBM Plex Mono, monospace",
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: "rgba(255,255,255,0.04)" },
        horzLines: { color: "rgba(255,255,255,0.04)" },
      },
      crosshair: { mode: CrosshairMode.Normal },
      localization: { locale: "it-IT" },
      rightPriceScale: { borderColor: "rgba(255,255,255,0.1)", scaleMargins: { top: 0.15, bottom: 0.15 } },
      timeScale: { borderColor: "rgba(255,255,255,0.1)", timeVisible: true, secondsVisible: false, barSpacing: 10 },
      width: containerRef.current.clientWidth,
      height: 420,
    });

    const series = chart.addCandlestickSeries({
      upColor: "#00C176",
      downColor: "#F6465D",
      borderUpColor: "#00C176",
      borderDownColor: "#F6465D",
      wickUpColor: "#00C176",
      wickDownColor: "#F6465D",
    });
    series.setData(data || []);

    // Scale guard: only draw levels/markers that are plausible against the REAL candle scale
    // (Capital.com). An order-of-magnitude mismatch (bad price) would otherwise blow up the
    // price axis and squash the candles into an unreadable strip.
    const lows = (data || []).map((d) => d.low).filter((v) => v != null && isFinite(v));
    const highs = (data || []).map((d) => d.high).filter((v) => v != null && isFinite(v));
    const mid = lows.length && highs.length ? (Math.min(...lows) + Math.max(...highs)) / 2 : 0;
    const inScale = (v) => {
      if (v == null || !isFinite(v)) return false;
      if (!mid) return true;
      const r = v / mid;
      return r >= 0.5 && r <= 2;
    };

    (priceLines || []).filter((pl) => inScale(pl.price)).forEach((pl) => {
      series.createPriceLine({
        price: pl.price,
        color: pl.color,
        lineWidth: 1,
        lineStyle: pl.lineStyle ?? 2,
        axisLabelVisible: true,
        title: pl.title,
      });
    });
    chart.timeScale().fitContent();

    const times = (data || []).map((d) => d.time);
    const snap = (t) => {
      if (!times.length || !t) return t;
      let best = times[0], bestd = Math.abs(times[0] - t);
      for (const tt of times) {
        const dd = Math.abs(tt - t);
        if (dd < bestd) { bestd = dd; best = tt; }
      }
      return best;
    };

    if (markers && markers.length) {
      // Markers carrying an explicit `price` are anchored to that exact price via a
      // transparent 1-point line series so they line up with their price line (e.g. Entry).
      markers
        .filter((m) => m.price != null && inScale(m.price))
        .forEach((m) => {
          const t = snap(m.time);
          if (!t) return;
          const anchor = chart.addLineSeries({
            color: "rgba(0,0,0,0)", lineWidth: 1,
            lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false,
          });
          anchor.setData([{ time: t, value: m.price }]);
          anchor.setMarkers([{ time: t, position: m.position, color: m.color, shape: m.shape, text: m.text }]);
        });
      // Remaining markers stay attached to the candle series.
      const mk = markers
        .filter((m) => m.price == null)
        .map((m) => ({ time: snap(m.time), position: m.position, color: m.color, shape: m.shape, text: m.text }))
        .filter((m) => m.time)
        .sort((a, b) => a.time - b.time);
      if (mk.length) series.setMarkers(mk);
    }

    (trades || []).forEach((t) => {
      const startT = snap(t.startTime);
      const endT = snap(t.endTime);
      // Anchor the dots to the REAL trade levels: entry price at entry, and the exact TP/SL
      // price at exit. A trade closes AT its TP/SL level by definition, so the exit dot must sit
      // on that line (e.g. a SELL's TP is always BELOW entry) — never at the candle close, which
      // can land on the wrong side of the entry and look impossible.
      const startPrice = t.startPrice;
      const endPrice = t.endPrice;
      // Skip the trade overlay entirely if either endpoint is off-scale (bad data): drawing it
      // would drag the price axis and deform the candles.
      if (!inScale(startPrice) || !inScale(endPrice)) return;
      const pts = [
        { time: startT, value: startPrice },
        { time: endT, value: endPrice },
      ].filter((p) => p.time).sort((a, b) => a.time - b.time);
      if (pts.length < 2 || pts[0].time === pts[1].time) return;
      const line = chart.addLineSeries({
        color: t.color, lineWidth: 2, lineStyle: 2,
        lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false,
      });
      line.setData(pts);

      const buy = t.direction === "BUY";
      const tradeMarkers = [
        { time: startT, position: buy ? "belowBar" : "aboveBar", color: t.color,
          shape: buy ? "arrowUp" : "arrowDown", text: `${t.direction} ${t.timeframe}` },
        { time: startT, position: "inBar", color: t.color, shape: "circle" },
        { time: endT, position: "inBar", color: t.color, shape: "circle", text: t.exitLabel },
      ].filter((m) => m.time).sort((a, b) => a.time - b.time);
      line.setMarkers(tradeMarkers);
    });

    const handleResize = () => {
      if (containerRef.current) chart.applyOptions({ width: containerRef.current.clientWidth });
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      chart.remove();
    };
  }, [data, priceLines, markers, trades]);

  return <div ref={containerRef} data-testid="candlestick-chart" className="w-full" />;
}
